import Image from '@/components/Image';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useCustomerService } from '@/hooks/useCustomerService';
import appCommunicationManager from '@/utils/app-communication-manager';
import { IS_DE_FR } from '@/utils/constants';
import { EAppCommunicationFuncName } from '@/utils/enums/app-sdk';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import { useTranslations } from '@/i18n/use-translations';
import { useContext } from 'react';

interface ContactListProps {
  isMobile?: boolean;
  className?: string;
}

export default function ContactList({ isMobile = false, className = '' }: ContactListProps) {
  const t = useTranslations('contact-zeelool.contactList');
  const { deviceType } = useContext(ResponsiveContext);
  const inAppH5 = [EPlatform.ANDROID, EPlatform.IOS].includes(deviceType);

  // 使用客服服务 Hook（只提供打开客服的函数）
  const { openCustomerService } = useCustomerService({
    inAppH5,
  });

  // 处理电话拨打
  const handlePhoneCall = () => {
    if (isMobile) {
      // 移动端APP内处理
      if (typeof window !== 'undefined') {
        if (inAppH5) {
          appCommunicationManager.sendMessage(EAppCommunicationFuncName.CALL_MOBILE, {
            to: '0013029900619',
          });
          return;
        }
      }
    }
    // 普通浏览器处理
    window.location.href = 'tel://0013029900619';
  };

  // 处理在线聊天
  const handleLiveChat = () => {
    openCustomerService();
  };

  // 处理帮助中心跳转
  const handleHelpCenter = () => {
    window.open(env.NEXT_PUBLIC_HELP_CENTER_URL ?? 'https://help.zeelool.com/hc/en-us/', '_blank');
  };

  const MobileContent = () => {
    // 移动端布局 - 精确匹配原Vue版本
    return (
      <div className={`bg-[#f5f6f7] px-10 py-24 ${className}`}>
        {/* 电话卡片 */}
        <div
          className="bg-white rounded-lg py-[17px] px-16 mb-8 flex items-center cursor-pointer"
          onClick={handlePhoneCall}
        >
          <Image
            src="/contact-zeelool/phone.png"
            alt={t('phoneAlt')}
            className="w-32 h-32 block mr-16"
            width={32}
            height={32}
          />
          <p className="text-base font-bb text-[#282828] leading-4">{t('hotline')}</p>
          <span className="ml-auto text-base font-normal text-[#282828] leading-4">
            {t('phoneNumber')}
          </span>
        </div>

        {/* 在线聊天卡片 */}
        <div
          className="bg-white rounded-lg py-[17px] px-16 mb-8 flex items-center cursor-pointer"
          onClick={handleLiveChat}
        >
          <Image
            src="/contact-zeelool/chat.png"
            alt={t('liveChatAlt')}
            className="w-32 h-32 block mr-16"
            width={32}
            height={32}
          />
          <p className="text-base font-bb text-[#282828] leading-4">{t('liveChat')}</p>
        </div>

        {/* 帮助中心卡片 */}
        <div
          className="bg-white rounded-lg py-[17px] px-16 mb-8 flex items-center cursor-pointer"
          onClick={handleHelpCenter}
        >
          <Image
            src="/contact-zeelool/help.png"
            alt={t('helpCenterAlt')}
            className="w-32 h-32 block mr-16"
            width={32}
            height={32}
          />
          <p className="text-base font-bb text-[#282828] leading-4">{t('helpFaqs')}</p>
        </div>
      </div>
    );
  };

  // 桌面端布局
  const DesktopContent = () => {
    return (
      <div className={`w-full bg-[#f5f6f7] flex items-center justify-center ${className}`}>
        <div className="flex items-center justify-between space-x-16 w-full  relative max-w-[1440px] px-32 py-40 ">
          {/* 电话卡片 */}
          <div className=" w-1/3 bg-white rounded-lg flex flex-col items-center  px-16 py-32">
            <Image
              src="/contact-zeelool/phone.png"
              alt={t('phoneAlt')}
              className="w-56 h-56 mt-32"
              width={56}
              height={56}
            />
            <h4 className="mt-20 text-2xl font-bb text-[#282828] leading-24 mb-16">
              {t('phoneNumber')}
            </h4>
            <p className="text-sm font-br text-[#808080] leading-[18px] text-center mb-16">
              {env.NEXT_PUBLIC_CUSTOMER_SERVICE_TIME}
            </p>
            <p className="h-18">&nbsp;</p>

            <button
              onClick={handlePhoneCall}
              className={`${IS_DE_FR ? 'w-[200px]' : 'w-[180px]'}  h-36 bg-[#463ae8] text-white rounded-md text-base font-bb hover:bg-[#3d32d1] transition-colors cursor-pointer`}
            >
              {t('makeACall')}
            </button>
          </div>

          {/* 在线聊天卡片 */}
          <div className=" w-1/3 bg-white rounded-lg flex flex-col items-center  px-16 py-32">
            <Image
              src="/contact-zeelool/chat.png"
              alt={t('liveChatAlt')}
              className="w-56 h-56 mt-32"
              width={56}
              height={56}
            />
            <h4 className="mt-20 text-2xl font-bb text-[#282828] leading-24 mb-16">
              {t('liveChat')}
            </h4>
            <p className="text-sm font-br text-[#808080] leading-[18px] text-center mb-16">
              {env.NEXT_PUBLIC_CUSTOMER_SERVICE_TIME}
            </p>
            <p className="h-18">&nbsp;</p>
            <button
              onClick={handleLiveChat}
              className={`${IS_DE_FR ? 'w-[220px]' : 'w-[180px]'} h-36 bg-[#463ae8] text-white rounded-md text-base font-bb hover:bg-[#3d32d1] transition-colors cursor-pointer`}
            >
              {t('startALiveChat')}
            </button>
          </div>

          {/* 帮助中心卡片 */}
          <div className=" w-1/3 bg-white rounded-lg flex flex-col items-center px-16 py-32">
            <Image
              src="/contact-zeelool/help.png"
              alt={t('helpCenterAlt')}
              className="w-56 h-56 mt-32"
              width={56}
              height={56}
            />
            <h4 className="mt-20 text-2xl font-bb text-[#282828] leading-24 mb-16">
              {t('helpFaqs')}
            </h4>
            <p className="text-sm font-br text-[#808080] leading-[18px] text-center mb-16">
              {t('helpCenterDescription')}
            </p>
            <button
              onClick={handleHelpCenter}
              className={`${IS_DE_FR ? 'w-[200px]' : 'w-[180px]'} h-36 bg-[#463ae8] text-white rounded-md text-base font-bb hover:bg-[#3d32d1] transition-colors cursor-pointer`}
            >
              {t('goToHelpCenter')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return <>{isMobile ? <MobileContent /> : <DesktopContent />}</>;
}
